import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from 'environments-lib';
import { IAuthService, IEnvironment, RequestConfigProperties } from 'interfaces-lib';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { Observable, throwError } from 'rxjs';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private serviceProviderFactory: SharedServiceProviderServiceFactory,
    private messageNotificationService: MessageNotificationService,
    private identityStorage: IdentityStorage,
    @Inject(ENVIRONMENT) protected environment: Partial<IEnvironment>
  ) {
    console.log('AuthInterceptor constructed.');
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (AuthInterceptor.authRequest(req.url, this.environment)) {
      return next.handle(req);
    }

    const authService = this.serviceProviderFactory.getServiceProviderService(this.identityStorage.get()?.namespace).getAuthService();

    if (!authService) {
      throw new Error('Cannot find appropriate authentication service.');
    }

    return new Observable((subscriber) => {
      authService.getToken().subscribe(
        (token) => {
          if (!token) {
            subscriber.error(new Error('Authentication token is not available'));
            return;
          }

          const requestProperties = this.getRequestProperties(req, token, authService);
          if (requestProperties) {
            req = req.clone(requestProperties);
          } else {
            req = req.clone();
          }

          next.handle(req).subscribe(
            (resp) => subscriber.next(resp),
            (error) => {
              subscriber.error(error);
              // return throwError(error);    // this does work, but not sure if it should be used like this
            },
            () => subscriber.complete()
          );
        },
        (error) => {
          this.messageNotificationService.error('An error occurred during authentication.', error, undefined, ErrorSeverity.CRITICAL);
          return throwError(error);
        }
      );
    });
  }

  private getRequestProperties(req: HttpRequest<any>, token: string, authService: IAuthService): RequestConfigProperties | undefined {
    // authentication services can supply a complete request properties (e.g. parameters, headers, etc.)
    let requestProperties = authService.getRequestProperties(req, token);

    // if the authentication service took no action we still need the token on calls to the kp server
    if (!requestProperties && AuthInterceptor.isCallToKpHost(req.url, this.environment)) {
      console.debug('Interceptor setting headers for request.');
      requestProperties = { setHeaders: { Authorization: 'bearer ' + token } };
    }

    // may be undefined
    return requestProperties;
  }

  private static authRequest(url: string, environment: Partial<IEnvironment>): boolean {
    return url.startsWith(`${environment.kpHost}/auth/`);
  }

  private static isCallToKpHost(url: string, environment: Partial<IEnvironment>): boolean {
    const hosts = [environment.kpHost, ...(environment.kpAdditionalAuthHosts ?? [])]
      .filter((h): h is string => !!h);
    return hosts.some((host) => url.startsWith(host));
  }
}
